import { useApolloClient } from "@apollo/client";
import { Modal } from "@msys/ui";
import { Stack } from "@mui/material";
import { useTranslate } from "@tolgee/react";
import { Form, Formik } from "formik";
import { uniqueId } from "lodash-es";
import React from "react";
import * as Yup from "yup";
import { TextField } from "../../commons/form-fields/TextField.js";
import { useDeclinePendingDocShareMutation } from "./DeclinePendingDocShare.generated.js";

interface FormValues {
  reason: string;
}

interface Props {
  pendingDocShareId: string;
  handleClose: () => void;
  handleDeclined: () => void;
}

// The reason is optional; a blank field is sent as the empty string.
export const DeclinePendingDocShareModal = ({
  pendingDocShareId,
  handleClose,
  handleDeclined,
}: Props) => {
  const { t } = useTranslate(["QuoteDecline", "Global"]);
  const client = useApolloClient();
  const [declinePendingDocShare, { loading }] =
    useDeclinePendingDocShareMutation({ client });

  const formId = React.useMemo(() => uniqueId(), []);

  const validationSchema = Yup.object().shape({
    reason: Yup.string().label(t("Reason", { ns: "QuoteDecline" })),
  });

  const onSubmit = async (values: FormValues) => {
    await declinePendingDocShare({
      variables: { input: { id: pendingDocShareId, reason: values.reason } },
    });
    handleDeclined();
  };

  return (
    <Modal
      title={t("Decline", { ns: "Global" })}
      handleClose={handleClose}
      actionButtons={[
        {
          label: t("Cancel", { ns: "Global" }),
          handleClick: handleClose,
          buttonProps: { variant: "text" },
        },
        {
          label: t("Decline", { ns: "Global" }),
          buttonProps: {
            type: "submit",
            form: formId,
            loading,
          },
        },
      ]}
    >
      <Formik<FormValues>
        onSubmit={onSubmit}
        validationSchema={validationSchema}
        initialValues={{ reason: "" }}
      >
        <Form id={formId}>
          <Stack spacing={1} direction="column">
            <TextField
              autoFocus
              label={t("Reason", { ns: "QuoteDecline" })}
              name="reason"
              multiline
              rows={3}
            />
          </Stack>
        </Form>
      </Formik>
    </Modal>
  );
};
